import { Box, Paper, Skeleton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { blue, green, grey, orange, purple } from "@mui/material/colors";
import { WasteSubmissionsStaffSummaryQuery, SubmissionStatus } from "generated-graphql/graphql";
import { useMemo } from "react";

type Props = {
  data?: WasteSubmissionsStaffSummaryQuery["wasteSubmissionsStaffSummary"];
  loading: boolean;
  onStateClick?: (state: string) => void;
  selectedState?: string | null;
};

// Status colors matching StatusDonutChart
const STATUS_COLORS: Record<string, string> = {
  NOT_STARTED: orange[400],
  IN_PROGRESS: blue[400],
  ENCAMP_INITIAL_REVIEW: purple[300],
  IN_REVIEW: blue[200],
  PROCESSING_SUBMISSION: purple[500],
  FILING_COMPLETE: green[400],
  NOT_REPORTING: grey[400],
};

const STATUS_LABELS: Record<string, string> = {
  NOT_STARTED: "Not Started",
  IN_PROGRESS: "In Progress",
  ENCAMP_INITIAL_REVIEW: "Initial Review",
  IN_REVIEW: "In Review",
  PROCESSING_SUBMISSION: "Processing",
  FILING_COMPLETE: "Complete",
  NOT_REPORTING: "Not Reporting",
};

// Order statuses for consistent stacking (most actionable first)
const STATUS_ORDER: SubmissionStatus[] = [
  SubmissionStatus.NotStarted,
  SubmissionStatus.InProgress,
  SubmissionStatus.EncampInitialReview,
  SubmissionStatus.InReview,
  SubmissionStatus.ProcessingSubmission,
  SubmissionStatus.FilingComplete,
  SubmissionStatus.NotReporting,
];

export function StateBarChart({ data, loading, onStateClick, selectedState }: Props) {
  const theme = useTheme();

  const stateData = useMemo(() => {
    if (!data?.byStateAndStatus) return [];
    return data.byStateAndStatus;
  }, [data]);

  const maxCount = useMemo(() => {
    if (!stateData.length) return 0;
    return Math.max(...stateData.map((s) => s.total));
  }, [stateData]);

  const handleStateClick = (state: string) => {
    if (onStateClick) {
      onStateClick(state);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        padding: 3,
        height: "100%",
        minHeight: 320,
      }}
    >
      {loading ? (
        <Box
          sx={{
            height: "100%",
            animation: "fadeIn 0.3s ease-in-out",
            "@keyframes fadeIn": {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.secondary }}>
            Top States by Status
          </Typography>
          <Stack spacing={1.5}>
            {Array.from({ length: 10 }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  animation: `slideIn 0.3s ease-out ${index * 0.05}s both`,
                  "@keyframes slideIn": {
                    from: {
                      opacity: 0,
                      transform: "translateX(-10px)",
                    },
                    to: {
                      opacity: 1,
                      transform: "translateX(0)",
                    },
                  },
                }}
              >
                <Skeleton variant="text" width={30} height={24} sx={{ flexShrink: 0 }} />
                <Skeleton
                  variant="rectangular"
                  sx={{ flex: 1, maxWidth: `${100 - index * 8}%` }}
                  height={24}
                />
                <Skeleton variant="text" width={40} height={24} sx={{ flexShrink: 0 }} />
              </Box>
            ))}
          </Stack>
        </Box>
      ) : (
        <Box
          sx={{
            height: "100%",
            animation: "fadeIn 0.5s ease-in-out",
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translateY(10px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Typography variant="body1" sx={{ mb: 2, color: theme.palette.text.secondary }}>
            Top States by Status
          </Typography>

          {/* Legend */}
          <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: "wrap", gap: 0.5 }}>
            {STATUS_ORDER.filter((status) => {
              // Only show statuses that have at least one count across all states
              return stateData.some((s) =>
                s.byStatus.some((bs) => bs.status === status && bs.count > 0)
              );
            }).map((status) => (
              <Stack key={status} direction="row" alignItems="center" spacing={0.5}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: 0.5,
                    backgroundColor: STATUS_COLORS[status],
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {STATUS_LABELS[status]}
                </Typography>
              </Stack>
            ))}
          </Stack>

          {stateData.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No data available
            </Typography>
          ) : (
            <Stack spacing={1}>
              {stateData.map((stateItem) => {
                const isSelected = !selectedState || selectedState === stateItem.state;
                const barWidth = (stateItem.total / maxCount) * 100;

                // Build ordered status segments
                const segments = STATUS_ORDER.map((status) => {
                  const statusData = stateItem.byStatus.find((bs) => bs.status === status);
                  return {
                    status,
                    count: statusData?.count ?? 0,
                    percentage:
                      stateItem.total > 0 ? ((statusData?.count ?? 0) / stateItem.total) * 100 : 0,
                  };
                }).filter((s) => s.count > 0);

                return (
                  <Box
                    key={stateItem.state}
                    onClick={() => handleStateClick(stateItem.state)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      cursor: "pointer",
                      opacity: isSelected ? 1 : 0.4,
                      transition: "opacity 0.2s ease-in-out",
                      "&:hover": {
                        opacity: 1,
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        width: 30,
                        fontWeight: selectedState === stateItem.state ? 600 : 400,
                      }}
                    >
                      {stateItem.state}
                    </Typography>
                    <Tooltip
                      title={
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight={600}>
                            {stateItem.state}: {stateItem.total.toLocaleString()} total
                          </Typography>
                          {segments.map((seg) => (
                            <Typography key={seg.status} variant="caption">
                              {STATUS_LABELS[seg.status]}: {seg.count.toLocaleString()}
                            </Typography>
                          ))}
                        </Stack>
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        sx={{
                          flex: 1,
                          height: 20,
                          backgroundColor: theme.palette.surfaceSubtle,
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${barWidth}%`,
                            height: "100%",
                            display: "flex",
                            borderRadius: 1,
                            overflow: "hidden",
                            transition: "width 0.3s ease-in-out",
                          }}
                        >
                          {segments.map((seg, index) => (
                            <Box
                              key={seg.status}
                              sx={{
                                width: `${seg.percentage}%`,
                                height: "100%",
                                backgroundColor: STATUS_COLORS[seg.status],
                                // Add subtle border between segments
                                borderRight:
                                  index < segments.length - 1
                                    ? `1px solid ${theme.palette.background.paper}`
                                    : "none",
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Tooltip>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        minWidth: 50,
                        textAlign: "right",
                        fontWeight: selectedState === stateItem.state ? 600 : 400,
                      }}
                    >
                      {stateItem.total.toLocaleString()}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      )}
    </Paper>
  );
}
